/**
 * Pure status classification functions for Måltall widgets.
 *
 * Two axes:
 *   - Billing rate:  value vs target (≥target = I mål, ≥75% of target = Nær mål, else Under mål)
 *   - Budget deviation: avvik % (≤0 = Under budsjett/I mål, ≤+5 = Nær mål, else Over mål)
 */

export type StatusTone = 'reached' | 'near' | 'miss';

/**
 * VIB-1450: Én semantisk KPI-palett på tvers av dashboards og rapporter.
 * Grønn = positivt/i mål, oransje = advarsel eller nøytral prognose, og
 * rødt = negativt/utenfor mål. Fargene skal ikke velges lokalt i widgets.
 */
export const KPI_SEMANTIC_COLORS = {
  positive: '#00AF68',
  positiveText: '#00875A',
  warning: '#E0922F',
  warningText: '#C77A1E',
  negative: '#FF8888',
  negativeText: '#E06A6A',
  track: '#E7ECEC',
} as const;

export interface StatusResult {
  tone: StatusTone;
  label: string;
}

export interface BonusMetricStatusInput {
  kind: 'cumulative' | 'state';
  achieved: boolean;
  progress: number;
  target: number;
  forecastTotal?: number | null;
}

/**
 * Delt badge-semantikk for bonusmål.
 *
 * Akkumulerende mål er bare «Oppnådd» når faktisk grunnlag har passert målet;
 * en prognose kan aldri utløse grønn badge eller bonus. Tilstandsmål kan
 * svinge igjen og beskrives derfor som innenfor/utenfor måltallet.
 */
export function bonusMetricStatus({
  kind,
  achieved,
  progress,
  target,
  forecastTotal,
}: BonusMetricStatusInput): StatusResult {
  if (kind === 'state') {
    if (achieved) return { tone: 'reached', label: 'Innenfor måltall' };
    if (progress >= 75) return { tone: 'near', label: 'Nær måltall' };
    return { tone: 'miss', label: 'Utenfor måltall' };
  }

  if (achieved) return { tone: 'reached', label: 'Oppnådd' };
  if (forecastTotal == null) return { tone: 'miss', label: 'Ikke nok data' };
  if (target > 0 && forecastTotal >= target) {
    return { tone: 'near', label: 'Prognose i mål' };
  }
  if (target > 0 && forecastTotal / target >= 0.75) {
    return { tone: 'near', label: 'På vei mot målet' };
  }
  return { tone: 'miss', label: 'Ikke oppnådd' };
}

/** Billing rate status. Both value and target are percentages (0–100+). */
export function billingStatus(value: number, target: number): StatusResult {
  if (value >= target) return { tone: 'reached', label: 'I mål' };
  if (target > 0 && value >= target * 0.75) return { tone: 'near', label: 'Nær mål' };
  return { tone: 'miss', label: 'Under mål' };
}

/**
 * Budget deviation status. avvik = (brukte − solgte) / solgte × 100.
 * Negative = under budget = favorable.
 */
export function deviationStatus(avvik: number): StatusResult {
  if (avvik <= 0) return { tone: 'reached', label: 'Under budsjett' };
  if (avvik <= 5) return { tone: 'near', label: 'Innenfor mål (≤ +5 %)' };
  return { tone: 'miss', label: 'Over mål' };
}

/** Tailwind classes for colored text by tone. */
export const TONE_TEXT: Record<StatusTone, string> = {
  reached: 'text-[#00875A]',
  near: 'text-[#C77A1E]',
  miss: 'text-[#E06A6A]',
};

/** Tailwind classes for solid fill by tone (bars, dots). */
export const TONE_BG: Record<StatusTone, string> = {
  reached: 'bg-[#00AF68]',
  near: 'bg-[#E0922F]',
  miss: 'bg-[#FF8888]',
};

/** Tailwind classes for a tinted surface (bg + border) by tone, e.g. list rows. */
export const TONE_SURFACE: Record<StatusTone, string> = {
  reached: 'bg-[rgba(0,175,104,0.12)] border-[#00AF68]/40',
  near: 'bg-[rgba(224,146,47,0.13)] border-[#E0922F]/40',
  miss: 'bg-[#E06A6A]/[0.12] border-[#E06A6A]/50',
};

/** Tailwind classes for pill (bg + text) by tone. */
export const TONE_PILL: Record<StatusTone, string> = {
  reached: 'bg-[rgba(0,175,104,0.12)] text-[#00875A]',
  near: 'bg-[rgba(224,146,47,0.13)] text-[#C77A1E]',
  miss: 'bg-[#E06A6A]/12 text-[#E06A6A]',
};
